import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Zap, Target, Calendar } from 'lucide-react';
import type { TimelineEvent } from '../../types/analytics';

interface TimelineProps {
  events: TimelineEvent[];
}

export const Timeline: React.FC<TimelineProps> = ({ events }) => {
  const getIcon = (type: TimelineEvent['type']) => {
    switch (type) {
      case 'first-visit':
        return <Calendar className="h-5 w-5 text-primary-600" />;
      case 'achievement':
        return <Award className="h-5 w-5 text-[var(--mustard)]" />;
      case 'milestone':
        return <Target className="h-5 w-5 text-sage-600" />;
      case 'framework-mastery':
        return <Zap className="h-5 w-5 text-coral-600" />;
    }
  };

  const getIconBg = (type: TimelineEvent['type']) => {
    switch (type) {
      case 'first-visit':
        return 'bg-primary-100';
      case 'achievement':
        return 'bg-[var(--mustard-dim)]';
      case 'milestone':
        return 'bg-sage-100';
      case 'framework-mastery':
        return 'bg-coral-100';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="space-y-4">
      {events.map((event, index) => (
        <div key={index} className="flex gap-4">
          {/* Icon */}
          <div className={`flex-shrink-0 w-10 h-10 rounded-full ${getIconBg(event.type)} flex items-center justify-center`}>
            {getIcon(event.type)}
          </div>
          
          {/* Content */}
          <div className="flex-1 pb-4 border-b border-[var(--rule)] last:border-0">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                {event.frameworkId ? (
                  <Link
                    to={`/elevation/entry/${event.frameworkId}`}
                    className="font-semibold text-charcoal-900 hover:text-primary-600 transition-colors"
                  >
                    {event.title}
                  </Link>
                ) : (
                  <h4 className="font-semibold text-charcoal-900">{event.title}</h4>
                )}
                <p className="text-sm text-charcoal-600 mt-1">{event.description}</p>
              </div>
              <span className="text-xs text-charcoal-500 ml-4 whitespace-nowrap">
                {formatDate(event.date)}
              </span>
            </div>
          </div>
        </div>
      ))}
      
      {events.length === 0 && (
        <div className="text-center py-8 text-charcoal-500">
          <Calendar className="h-12 w-12 mx-auto mb-3 text-[var(--text-ter)]" />
          <p>Your journey timeline will appear here.</p>
          <p className="text-sm mt-2">Keep exploring to build your story!</p>
        </div>
      )}
    </div>
  );
};

